// Which hosts may receive a sign-in handoff (`#__symbols_auth=` fragment —
// the user's access + refresh token, see ./authHandoff.js).
//
// A handoff hands the FULL platform session to the receiving origin, so the
// receiving page can do anything the user can. Only two classes qualify:
//
//   (a) the same origin (incl. relative paths) — the page already holds it;
//   (c) localhost / 127.0.0.1 / *.localhost — from a LOCAL page only.
//
// Every remote host is refused: preview hosts (bare or `<owner>--<key>`) run
// user project code, and other shells (demo) are not handoff targets.
// `confirm` is no longer produced; callers keep handling it so the
// project-scoped preview token can reuse the path later.
//
// Returns `{ verdict: 'trusted' | 'confirm' | 'refused', projects }`.

export const classifyHandoffTarget = (url) => {
  const refused = { verdict: 'refused', projects: [] }
  const trusted = { verdict: 'trusted', projects: [] }
  const isLocalHost = (h) =>
    h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || (!!h && h.endsWith('.localhost'))

  if (typeof url !== 'string' || !url) return refused
  const here = typeof window !== 'undefined' ? window.location : null
  let u
  try {
    u = new URL(url, here?.href || undefined)
  } catch {
    return refused
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return refused
  if (here?.origin && u.origin === here.origin) return trusted
  if (!here) return refused

  // (c) local page → any local target. A deployed page never hands off to
  // a local one (a `?redirect=http://localhost:…` link would exfiltrate a
  // real session to whatever listens on that port).
  const localPage = isLocalHost(here.hostname)
  if (isLocalHost(u.hostname)) return localPage ? trusted : refused

  // Every remote target is refused (PROD P0 hotfix, Nika 2026-10-09:
  // "Refuse all previews" + "No, refuse demo"). A preview host — the bare
  // preview app (one origin for every project) or a per-project
  // `<owner>--<key>` host — runs USER code that reads that origin's
  // storage, and "the user can read this private project" is no trust: any
  // owner can add the victim by email. Another shell on the same API plane
  // (demo) is not a handoff target either. Preview sign-in returns only with
  // a server-minted project-scoped read token, never the platform session.
  return refused
}
